import React, { useEffect, useState } from "react";
import { CalendarX2, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatDateOnly, todayInput } from "@/lib/date";

const DEADLINE_DAYS = 2;
const MAX_DESC = 1000;
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const CONFLICT_STATUS = {
  pending: { label: "Pending", cls: "bg-amber-100 text-amber-700" },
  approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-700" },
  declined: { label: "Declined", cls: "bg-red-100 text-red-700" },
};

// Last day (YYYY-MM-DD, Central calendar) a conflict can be submitted for an event.
export function conflictDeadline(eventDate) {
  const [y, m, d] = eventDate.slice(0, 10).split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d - DEADLINE_DAYS));
  return dt.toISOString().slice(0, 10);
}

export const prettyYmd = (s) => {
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  return `${DOW[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]}, ${MON[m - 1]} ${d}, ${y}`;
};

export function ConflictStatusChip({ status, testId }) {
  const meta = CONFLICT_STATUS[status] || CONFLICT_STATUS.pending;
  return <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.cls}`} data-testid={testId}>Conflict {meta.label.toLowerCase()}</span>;
}

export default function ConflictDialog({ event, open, onClose, onSaved }) {
  const [desc, setDesc] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setDesc(""); }, [open]);
  if (!event) return null;
  const deadline = conflictDeadline(event.event_date);
  const closed = todayInput() > deadline;

  const submit = async () => {
    if (!desc.trim()) { toast.error("Please describe your conflict."); return; }
    setSaving(true);
    try {
      await api.post("/conflicts", { event_id: event.id, description: desc.trim() });
      toast.success("Conflict submitted — your coach will review it.");
      onSaved?.(); onClose?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="conflict-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><CalendarX2 className="h-5 w-5 text-orange-500" /> Report a conflict</DialogTitle>
          <DialogDescription data-testid="conflict-event-label">{event.title} · {formatDateOnly(event.event_date)}</DialogDescription>
        </DialogHeader>
        {closed ? (
          <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="conflict-deadline-blocked">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>Conflict requests must be submitted at least {DEADLINE_DAYS} days before the rehearsal. The deadline for this one was <span className="font-semibold">{prettyYmd(deadline)} at 11:59 PM</span> (Central). Please speak with your coach directly.</p>
          </div>
        ) : (
          <p className="text-xs text-slate-500" data-testid="conflict-deadline-info">Submit by <span className="font-semibold text-slate-700">{prettyYmd(deadline)} at 11:59 PM</span> (Central) — at least {DEADLINE_DAYS} days before the rehearsal.</p>
        )}
        <div className="space-y-2">
          <Label htmlFor="conflict-desc">Describe your conflict</Label>
          <Textarea id="conflict-desc" rows={4} maxLength={MAX_DESC} value={desc} disabled={closed} onChange={(e) => setDesc(e.target.value)}
            placeholder="e.g. Doctor's appointment at 4:00 PM — I'll miss the first hour." data-testid="conflict-description-input" />
          <p className="text-right text-[11px] text-slate-400 tabular-nums">{desc.length}/{MAX_DESC}</p>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-full" onClick={() => onClose?.()} data-testid="conflict-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={closed || saving || !desc.trim()} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="conflict-submit-btn">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Submit conflict
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
